// Anbefalt termisk innhentingsvindu (GEO-15): når er termisk kontrast mellom
// mål og bakgrunn HØYEST, og når bør den unngås?
//
// ── HVA DETTE ER, OG HVA DET IKKE ER ────────────────────────────────────────
// Dette er en ANBEFALING bygget på ren sol-geometri fra `sun.ts` (`sunDay`),
// IKKE en varmemodell. Grunnloven §1 forbyr fabrikkert presisjon: modulen
// regner ALDRI faktisk temperatur, materialtermisk masse, vind eller
// skydekke — den regner bare NÅR sola var oppe, hvor lenge, og hvor langt
// unna soloppgang/-nedgang et tidspunkt er, og gir en tommelfingerregel
// bygget på kjent varmefysikk:
//
//   * Midt på dagen (rundt solmiddag) er innstråling på sitt sterkeste.
//     Asfalt, tak og annet lavspesifikk-varmekapasitet-underlag varmes da
//     RASKERE enn et menneske klarer å varme luften rundt seg — kontrasten
//     kollapser eller reverserer. Dette er kjernen i kortets NAI-eksempel
//     («asfalt varmere enn et menneske» midt på dagen).
//   * Rundt soloppgang og solnedgang KRYSSER bakgrunnstemperaturen målets
//     temperatur («termisk kryssing») — kontrasten går mot null i et par
//     timer omkring hver kryssing. Kortets eksempel («unngå 06:40–07:20»)
//     er nettopp morgenkryssingen for én bestemt dato/posisjon.
//   * Best kontrast er i mørkets midte: bakgrunnen har hatt tid til å
//     kjøle seg under målets temperatur, og morgenens oppvarming/kryssing
//     har ikke startet.
//
// Vinduene som returneres er derfor en anbefaling AVLEDET AV soltider, med
// et eksplisitt `grunnlag` (hvilke soltider ble brukt) og `forbehold` (hva
// modellen IKKE vet: vær, vind, skydekke, målets egen varmekapasitet). En
// operatør som trenger et hardere svar må måle — ikke lese av dette.
//
// Grensetilfeller (midnattssol/mørketid) håndteres eksplisitt: uten en reell
// solnedgang/-oppgang gir funksjonen tomme vinduer og sier hvorfor i stedet
// for å late som en kryssing fant sted.
import { sunDay } from './sun.js';
const H_MS = 3_600_000;
const DAY_MS = 86_400_000;
const STANDARD_OPTS = {
    kryssingEtterSolnedgangH: 2,
    kryssingForSoloppgangH: 1,
    kryssingEtterSoloppgangH: 1,
    middagHalvBreddeH: 3,
};
function fmt(ms) {
    return ms === null ? '—' : new Date(ms).toISOString().slice(11, 16) + 'Z';
}
/**
 * Anbefalt termisk innhentingsvindu for et punkt og UTC-døgn.
 *
 * Bygget UTENFOR MIDNATT: soldøgnet fra `sunDay` løper [solmidnatt, +24 t],
 * så «kvelden» og «morgenen etter» kommer i riktig kronologisk rekkefølge i
 * samme svar — nødvendig for at «2–5 t etter solnedgang» og «unngå rundt
 * soloppgang» skal kunne uttrykkes som ett sammenhengende anbefalt vindu.
 */
export function termiskInnhentingsvindu(timeMs, lat, lon, opts) {
    const o = { ...STANDARD_OPTS, ...opts };
    const day = sunDay(timeMs, lat, lon);
    const { sunsetMs, solarNoonMs, state } = day;
    const unngaaVindu = [];
    const anbefaltVindu = [];
    const grunnlagDeler = [];
    // Natten mellom kveldskryssing og morgenkryssing spenner over TO soldøgn:
    // `sunsetMs` hører til `day`, men morgenen som følger er NESTE soldøgn —
    // `day.sunriseMs` er tidligere SAMME morgen (før dagens solnedgang), ikke
    // morgenen etter. Uten dette hentes feil soloppgang, og «unngå»-vinduene
    // ender i feil rekkefølge (morgenkryssingen ville ligget FØR kveldens).
    const nesteDag = sunDay(day.solarNoonMs + DAY_MS, lat, lon);
    const sunriseMs = nesteDag.sunriseMs;
    // Midtdags-unngå-vindu finnes ALLTID (solmiddag er alltid definert, selv i
    // midnattssol/mørketid — der er det ganske enkelt tidspunktet for høyest
    // solhøyde/mest lys på himmelen).
    unngaaVindu.push({ startMs: solarNoonMs - o.middagHalvBreddeH * H_MS, sluttMs: solarNoonMs + o.middagHalvBreddeH * H_MS });
    grunnlagDeler.push(`Solmiddag ${fmt(solarNoonMs)} ± ${o.middagHalvBreddeH} t unngås (sterkest innstråling).`);
    if (sunsetMs === null || sunriseMs === null) {
        // Midnattssol: ingen solnedgang ⇒ ingen kveldskryssing å regne rundt, og
        // ingen "mørkets midte" — bare si det, ikke lat som et vindu fantes.
        // Mørketid: sola går aldri opp — hele døgnet er i prinsippet mørkt, men
        // uten en soloppgang å forankre morgenkryssingen i, er en anbefaling
        // her fabrikkert presisjon. Vi lar KUN midtdags-unngå-vinduet stå (det
        // representerer fortsatt "mest lys på himmelen", relevant selv om sola
        // ikke er over horisonten — jf. borgerlig tussmørke midt på dagen i
        // mørketid, se `lysforhold.ts` `tilstandForklaring`).
        grunnlagDeler.push(sunsetMs === null
            ? (state === 'midnattssol'
                ? 'Midnattssol: sola går aldri ned dette døgnet — ingen kveldskryssing å anbefale rundt.'
                : 'Mørketid: sola går aldri opp dette døgnet (ingen solnedgang å regne fra) — ingen '
                    + 'anbefalt vindu regnes ut.')
            : 'Sola går aldri opp morgenen etter (mørketid der) — ingen soloppgang å forankre '
                + 'morgenkryssingen i, så intet anbefalt vindu regnes ut.');
        return {
            dateUtc: day.dateUtc,
            day,
            anbefaltVindu,
            unngaaVindu,
            grunnlag: grunnlagDeler.join(' '),
            forbehold: FORBEHOLD,
        };
    }
    const kveldKryssingSlutt = sunsetMs + o.kryssingEtterSolnedgangH * H_MS;
    const morgenKryssingStart = sunriseMs - o.kryssingForSoloppgangH * H_MS;
    const morgenKryssingSlutt = sunriseMs + o.kryssingEtterSoloppgangH * H_MS;
    unngaaVindu.push({ startMs: sunsetMs, sluttMs: kveldKryssingSlutt });
    unngaaVindu.push({ startMs: morgenKryssingStart, sluttMs: morgenKryssingSlutt });
    unngaaVindu.sort((a, b) => a.startMs - b.startMs);
    grunnlagDeler.push(`Solnedgang ${fmt(sunsetMs)}: kveldskryssing unngås ${fmt(sunsetMs)}–${fmt(kveldKryssingSlutt)} `
        + `(${o.kryssingEtterSolnedgangH} t). Soloppgang ${fmt(sunriseMs)}: morgenkryssing unngås `
        + `${fmt(morgenKryssingStart)}–${fmt(morgenKryssingSlutt)}.`);
    // Anbefalt vindu = mørkets midte: fra kveldskryssingen er over til
    // morgenkryssingen starter. Kan bli tomt/negativt på høye breddegrader
    // sommertid der natta er kort — da anbefales ingenting, det er korrekt
    // (jf. kortets «2-5t» eksempel som forutsetter et reelt mørkevindu).
    if (kveldKryssingSlutt < morgenKryssingStart) {
        anbefaltVindu.push({ startMs: kveldKryssingSlutt, sluttMs: morgenKryssingStart });
        grunnlagDeler.push(`Anbefalt vindu: ${fmt(kveldKryssingSlutt)}–${fmt(morgenKryssingStart)} `
            + '(mørkets midte — etter kveldskryssing, før morgenkryssing).');
    }
    else {
        grunnlagDeler.push('Ingen anbefalt vindu: mørkeperioden er kortere enn kryssingsvinduene til sammen '
            + '(lys sommernatt/høy breddegrad) — kveldskryssing og morgenkryssing overlapper.');
    }
    return {
        dateUtc: day.dateUtc,
        day,
        anbefaltVindu,
        unngaaVindu,
        grunnlag: grunnlagDeler.join(' '),
        forbehold: FORBEHOLD,
    };
}
const FORBEHOLD = 'ANBEFALING, ikke måling: bygget på sol-geometri alene (soloppgang/-nedgang/solmiddag fra '
    + '`sunDay`), ikke på faktisk temperatur. Regner ikke med vær, vind, skydekke, nedbør, '
    + 'målets eller bakgrunnens varmekapasitet/materialer, eller lokal terrengskygge (se '
    + '`shadow.ts` for skyggevarighet). Klar/skyfri natt gir raskere bakgrunnsavkjøling og dermed '
    + 'TIDLIGERE god kontrast enn dette vinduet viser; skyet/vindfullt vær utjevner kontrasten og '
    + 'kan gjøre hele vinduet mindre nyttig. Kryssingsvarigheten (`kryssingEtterSolnedgangH` mfl.) '
    + 'er en generell tommelfingerregel, ikke stedsspesifikt målt — juster ved lokalkunnskap.';
//# sourceMappingURL=termisk.js.map